import { useCallback, useEffect, useState } from "react";
import { purchaseCatalogService } from "../services";
import { useDebouncedValue } from "./useDebouncedValue";

const DEFAULT_ROWS = 10;
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Server-backed purchase catalog for admin create PO (list vs search, category, pagination).
 */
export function usePurchaseCatalogQuery(orgId) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS);
  const [searchInput, setSearchInput] = useState("");
  const debouncedQ = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [category, setCategory] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setCategoriesLoading(true);
        const res = await purchaseCatalogService.getCategories();
        const list = res?.data || res || [];
        if (!cancelled) {
          setCategories(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.error("Failed to load catalog categories", e);
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    setPage(0);
  }, [orgId, debouncedQ, category]);

  const fetchCatalog = useCallback(async () => {
    if (!orgId) {
      setRows([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const apiPage = page + 1;
      const trimmedQ = debouncedQ.trim();
      const params = {
        page: apiPage,
        limit: rowsPerPage,
        category: category || undefined,
      };
      const res = trimmedQ
        ? await purchaseCatalogService.searchCatalog(orgId, {
            ...params,
            q: trimmedQ,
          })
        : await purchaseCatalogService.getCatalog(orgId, params);
      setRows(res?.data || []);
      setTotal(res?.pagination?.total ?? 0);
    } catch (e) {
      console.error("Failed to load purchase catalog", e);
      setError(
        e.response?.data?.message || e.message || "Failed to load products",
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [orgId, page, rowsPerPage, debouncedQ, category]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const handleChangePage = useCallback((_e, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchInput("");
    setCategory("");
    setPage(0);
  }, []);

  return {
    rows,
    total,
    loading,
    error,
    page,
    rowsPerPage,
    searchInput,
    setSearchInput,
    debouncedQ,
    category,
    setCategory,
    categories,
    categoriesLoading,
    setPage,
    handleChangePage,
    handleChangeRowsPerPage,
    resetFilters,
    refetch: fetchCatalog,
  };
}
